import { NavigateFunction } from "react-router-dom";
import { create } from "zustand";
import { createApi } from "./Components/utils/createApi";
import { universalAPIErrorHandler } from "./Components/utils/universalAPIErrorHandler";
import { AxiosError } from "axios";
import { logger } from "./utils/logger";

interface WireGuardOption {
  id: number;
  name: string;
  zone: string;
  // Returned by GET wireguard/ (WireGuardSerializer). The tunnel endpoints do not
  // carry it, so the Site About WireGuard tab resolves it from this list.
  server_public_key?: string;
}

interface AppState {
  refreshingToken: boolean;
  setRefreshingToken: (refreshingToken: boolean) => void;

  backdropLoadingScreenRefreshTokenOn: boolean;
  setBackdropLoadingScreenRefreshTokenOn: (
    backdropLoadingScreenRefreshTokenOn: boolean
  ) => void;

  updateUserStorageDateFormat: (
    navigate: NavigateFunction,
    formatBoolean: boolean
  ) => Promise<void>;

  dateMMDDYYYY: boolean;
  setDateMMDDYYYY: (dateMMDDYYYY: boolean) => void;

  userPermissions: any;
  setUserPermissions: (userPermissions: any) => void;

  navigatedToAlertsFromAnalytics: boolean;
  setNavigatedToAlertsFromAnalytics: (
    navigatedToAlertsFromAnalytics: boolean
  ) => void;

  wireGuardOptions: WireGuardOption[];
  setWireGuardOptions: (wireGuardOptions: WireGuardOption[]) => void;
  getWireGuardOptions: (navigate: NavigateFunction) => Promise<void>;

  groupTrees: any[] | undefined;
  setGroupTrees: (groupTrees: any[] | undefined) => void;
  groupTreesPatrolOnly: any[] | undefined;
  setGroupTreesPatrolOnly: (groupTreesPatrolOnly: any[] | undefined) => void;
}

// Get the initial date format preference from localStorage or default to true (MM/DD/YYYY)
const getInitialDateFormat = (): boolean => {
  const userStorage = JSON.parse(localStorage.getItem("userStorage") || "{}");
  if (userStorage?.data?.alert_date_format) {
    if (userStorage.data.alert_date_format === "DD/MM/YYYY") {
      return false;
    }
    if (userStorage.data.alert_date_format === "MM/DD/YYYY") {
      return true;
    }
    return true;
  } else {
    return true;
  }
};

export const useAppStore = create<AppState>((set, get) => ({
  refreshingToken: false,
  setRefreshingToken: (refreshingToken: boolean) => set({ refreshingToken }),

  backdropLoadingScreenRefreshTokenOn: false,
  setBackdropLoadingScreenRefreshTokenOn: (
    backdropLoadingScreenRefreshTokenOn: boolean
  ) => set({ backdropLoadingScreenRefreshTokenOn }),

  updateUserStorageDateFormat: async (
    navigate: NavigateFunction,
    formatBoolean: boolean
  ) => {
    const AxiosUI = createApi("");

    const retryFunction = () => {
      get().updateUserStorageDateFormat(navigate, formatBoolean);
    };

    const newDateFormat = formatBoolean ? "MM/DD/YYYY" : "DD/MM/YYYY";

    try {
      const response = await AxiosUI.patch("storage/user/", {
        data: { alert_date_format: newDateFormat },
      });
      localStorage.setItem("userStorage", JSON.stringify(response.data));
    } catch (reason) {
      universalAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {}
      );
    }
  },

  dateMMDDYYYY: getInitialDateFormat(),
  setDateMMDDYYYY: (dateMMDDYYYY: boolean) => {
    set({ dateMMDDYYYY });
  },

  userPermissions: {
    ailink: false,
    ailink_index: false,
    sentinel: false,
    smtp: false,
    chm: false,
    arm_disarm: false,
    add_cameras: false,
    edit_cameras: false,
    delete_cameras: false,
    manage_users: false,
    product_requirements: false,
    flex_schedule: false,
    view_empty_group: false,
    super_user: false,
    view_analytics: false,
    view_alerts: false,
    view_passwords: false,
    // Genetec direct video source: per-camera connection fields. Declared
    // from /user/info/; no route is gated on it yet.
    direct_camera_access: false,
    alert_muting: false,
    live_streaming: false,
    create_wireguard_tunnels: false,
    patrol_only: false,
    // ENG-1816: View Only screen list from /user/info/; empty = View All.
    visible_screens: [],
  },

  setUserPermissions: (userPermissions: any) => set({ userPermissions }),

  navigatedToAlertsFromAnalytics: false,
  setNavigatedToAlertsFromAnalytics: (
    navigatedToAlertsFromAnalytics: boolean
  ) => set({ navigatedToAlertsFromAnalytics }),

  wireGuardOptions: [],
  setWireGuardOptions: (wireGuardOptions: WireGuardOption[]) =>
    set({ wireGuardOptions }),

  getWireGuardOptions: async (navigate: NavigateFunction) => {
    const AxiosUI = createApi("");

    try {
      const response = await AxiosUI.get("wireguard/");
      set({ wireGuardOptions: response.data.results });
    } catch (reason) {
      // WireGuard options are non-critical (used only for Site About). Do not
      // call universalAPIErrorHandler here—it can redirect to /no-internet
      // and would incorrectly send users there on login when this runs before
      // auth is ready or when the request fails transiently.
      logger.warn(
        "Failed to load WireGuard options, leaving list empty:",
        reason
      );
    }
  },

  groupTrees: undefined,
  setGroupTrees: (groupTrees: any[] | undefined) => set({ groupTrees }),
  groupTreesPatrolOnly: undefined,
  setGroupTreesPatrolOnly: (groupTreesPatrolOnly: any[] | undefined) =>
    set({ groupTreesPatrolOnly }),
}));
